import { useCallback, useEffect, useRef, useState } from "react";
import { searchPages } from "@ludens-reklame/rubics-v3-sdk/page";
import {
  Page,
  PageSearchQuery,
} from "@ludens-reklame/rubics-v3-sdk/page/types";

const SEARCH_DEBOUNCE_MS = 300;
const FETCH_LIMIT = 200;

export type PageSortOrder = "date" | "random";

type Filters = Record<string, string[]>;

interface Options<PageProps> {
  baseQuery: Record<string, unknown>;
  requiredTags: string[];
  pageSize: number;
  fetchOnInit?: boolean;
  matchesFilters?: (page: Page<PageProps>, filters: Filters) => boolean;
  sortOrder?: PageSortOrder;
}

export function useTagFilteredSearch<PageProps>({
  baseQuery,
  requiredTags,
  pageSize,
  fetchOnInit = true,
  matchesFilters,
  sortOrder = "date",
}: Options<PageProps>) {
  const [results, setResults] = useState<Page<PageProps>[]>([]);
  const [visible, setVisible] = useState(pageSize);
  const [loading, setLoading] = useState(fetchOnInit);
  const [hasFetched, setHasFetched] = useState(false);
  const [unfilteredResults, setUnfilteredResults] =
    useState<Page<PageProps>[]>();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState<Filters>({});

  const debounce = useRef<number>();
  const latestRequest = useRef(0);
  const shuffleKeys = useRef(new Map<string, number>());

  const sort = useCallback(
    (pages: Page<PageProps>[]) => {
      if (sortOrder !== "random") return pages;

      const keys = shuffleKeys.current;
      const keyFor = (page: Page<PageProps>) => {
        if (!keys.has(page._id)) keys.set(page._id, Math.random());
        return keys.get(page._id)!;
      };

      return [...pages].sort((a, b) => keyFor(a) - keyFor(b));
    },
    [sortOrder]
  );

  const run = useCallback(
    async (filters: Filters, text: string) => {
      const activeGroups = Object.values(filters).filter(
        (values) => values.length > 0
      );
      const groups = matchesFilters ? [] : activeGroups;

      const sets = [...requiredTags.map((tag) => [tag]), ...groups];

      const queryFor = (tags?: string[]) =>
        ({
          ...baseQuery,
          limit: FETCH_LIMIT,
          ...(tags ? { tags } : {}),
          ...(text ? { text } : {}),
        } as PageSearchQuery);

      const request = ++latestRequest.current;
      setLoading(true);

      try {
        let pages: Page<PageProps>[];

        if (sets.length === 0) {
          pages = (await searchPages<PageProps>(queryFor())).results;
        } else {
          const responses = await Promise.all(
            sets.map((tags) => searchPages<PageProps>(queryFor(tags)))
          );
          const [first, ...rest] = responses;
          const others = rest.map(
            (response) => new Set(response.results.map((page) => page._id))
          );

          pages = first.results.filter((page) =>
            others.every((ids) => ids.has(page._id))
          );
        }

        if (request === latestRequest.current) {
          if (!text && !activeGroups.length) setUnfilteredResults(pages);
          if (matchesFilters) {
            pages = pages.filter((page) => matchesFilters(page, filters));
          }

          setResults(sort(pages));
          setVisible(pageSize);
          setHasFetched(true);
        }
      } finally {
        if (request === latestRequest.current) {
          setLoading(false);
        }
      }
    },
    [baseQuery, matchesFilters, pageSize, requiredTags, sort]
  );

  const initialised = useRef(false);
  useEffect(() => {
    if (fetchOnInit && !initialised.current) {
      initialised.current = true;
      run({}, "");
    }
  }, [fetchOnInit, run]);

  useEffect(() => () => window.clearTimeout(debounce.current), []);

  const onFilterChange = (filters: Filters, text: string) => {
    const isTyping = text !== searchQuery;

    setSearchQuery(text);
    setActiveFilters(filters);
    window.clearTimeout(debounce.current);

    if (isTyping) {
      debounce.current = window.setTimeout(
        () => run(filters, text),
        SEARCH_DEBOUNCE_MS
      );
    } else {
      run(filters, text);
    }
  };

  return {
    results: results.slice(0, visible),
    total: results.length,
    unfilteredResults,
    unfilteredTotal: unfilteredResults?.length,
    loading,
    hasFetched,
    hasMore: visible < results.length,
    loadMore: () => setVisible((count) => count + pageSize),
    activeFilters,
    searchQuery,
    onFilterChange,
  };
}
